import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { formatDate } from 'utils/lmmsHelpers';

// ============================================================================
// Types
// ============================================================================

export interface SystemConfigFormData {
  configValue: string;
  description: string;
}

export interface SystemConfigRecord {
  id: string;
  configKey: string;
  configValue: string;
  description: string;
  updatedBy: string;
  updatedAt: string;
}

interface SystemConfigEditModalProps {
  isOpen: boolean;
  record: SystemConfigRecord | null;
  formData: SystemConfigFormData;
  onFormDataChange: (data: SystemConfigFormData) => void;
  onSave: () => void;
  onClose: () => void;
}

interface SystemConfigViewModalProps {
  isOpen: boolean;
  record: SystemConfigRecord | null;
  onClose: () => void;
}

// ============================================================================
// Edit Modal — Glassmorphism Design
// ============================================================================

export const SystemConfigEditModal: React.FC<SystemConfigEditModalProps> = ({
  isOpen,
  record,
  formData,
  onFormDataChange,
  onSave,
  onClose,
}) => {
  if (!isOpen || !record) return null;

  const handleChange = (field: keyof SystemConfigFormData, value: string) => {
    onFormDataChange({ ...formData, [field]: value });
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-sm">
        <div className="modal-header">
          <h2 className="modal-title">Edit Configuration</h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div className="form-group">
            <label className="form-label">Config Key</label>
            <input
              type="text"
              className="form-input"
              value={record.configKey}
              readOnly
              style={{ border: '1px solid #334155', background: 'var(--bg-secondary)', cursor: 'not-allowed' }}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Config Value *</label>
            <input
              type="text"
              className="form-input"
              value={formData.configValue}
              onChange={(e) => handleChange('configValue', e.target.value)}
              placeholder="Enter value"
              style={{ border: '1px solid #334155' }}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-input"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Enter description"
              rows={3}
              style={{ border: '1px solid #334155', resize: 'vertical' }}
            />
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={onSave}>
            <IconSystem.save size={16} />
            Save Changes
          </button>
        </div>
      </div>
    </>
  );
};

// ============================================================================
// View Modal — Glassmorphism Design
// ============================================================================

export const SystemConfigViewModal: React.FC<SystemConfigViewModalProps> = ({
  isOpen,
  record,
  onClose,
}) => {
  if (!isOpen || !record) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        <div className="modal-header">
          <h2 className="modal-title">Configuration Details</h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <div>
              <label className="form-label">Config Key</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', fontWeight: 600 }}>
                {record.configKey}
              </div>
            </div>
            <div>
              <label className="form-label">Config Value</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', fontWeight: 600 }}>
                {record.configValue}
              </div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Description</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {record.description || '—'}
              </div>
            </div>
            <div>
              <label className="form-label">Updated By</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {record.updatedBy}
              </div>
            </div>
            <div>
              <label className="form-label">Updated At</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {formatDate(record.updatedAt)}
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </>
  );
};
